import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tabs,
  Tab,
  TableSortLabel,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Category as CategoryIcon,
  Business as BusinessIcon,
  Label as LabelIcon,
} from '@mui/icons-material';
import api from '../config/api.js';
import { formatCurrency } from '../utils/formatters';
import DatePickerField from '../components/common/DatePickerField';

const Analytics = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  // Get first and last day of current month
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

  // Format dates for input fields
  const formatDateForInput = (date) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  const [closes, setCloses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    dateType: 'submit',
    startDate: formatDateForInput(firstDayOfMonth),
    endDate: formatDateForInput(lastDayOfMonth),
  });
  const [activeTab, setActiveTab] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' });
  const [mappings, setMappings] = useState({});
  const [useNormalized, setUseNormalized] = useState(true);

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    try {
      const response = await api.get('/data-mappings');
      const mappingsData = response.data;

      // Create a lookup object for quick access
      const mappingsLookup = {
        carrier: {},
        product: {},
        lead_type: {},
        agency: {}
      };

      mappingsData.forEach(mapping => {
        mappingsLookup[mapping.type][mapping.original_value] = mapping.canonical_value;
      });

      setMappings(mappingsLookup);
    } catch (error) {
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/Closes');
      const closesData = response.data;

      if (!Array.isArray(closesData)) {
        throw new Error('Invalid data format received from server');
      }

      // Filter by date
      const filteredCloses = closesData.filter(close => {
        let matchesDate = true;
        if (filters.dateType && (filters.startDate || filters.endDate)) {
          const dateField = filters.dateType === 'submit' ? 'submitDate' : 'effectiveDate';
          const dateStr = close[dateField];

          if (!dateStr) {
            matchesDate = false;
          } else {
            const [datePart] = dateStr.split(' ');
            const [year, month, day] = datePart.split('-');
            const closeDate = new Date(year, month - 1, day);

            if (filters.startDate) {
              const startDate = new Date(filters.startDate);
              startDate.setHours(0, 0, 0, 0);
              matchesDate = matchesDate && closeDate >= startDate;
            }

            if (filters.endDate) {
              const endDate = new Date(filters.endDate);
              endDate.setHours(23, 59, 59, 999);
              matchesDate = matchesDate && closeDate <= endDate;
            }
          }
        }
        return matchesDate;
      });

      setCloses(filteredCloses);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Helper function to apply mappings with automatic case normalization
  const applyMapping = (value, type) => {
    if (!value) return 'Unknown';

    if (!useNormalized) return value;

    const trimmedValue = value.trim();

    // Check for explicit mapping first (case-sensitive)
    if (mappings[type]?.[trimmedValue]) {
      return mappings[type][trimmedValue];
    }

    // Check for case-insensitive mapping match
    const lowerValue = trimmedValue.toLowerCase();
    const matchingMapping = Object.entries(mappings[type] || {}).find(
      ([key, _]) => key.toLowerCase() === lowerValue
    );

    if (matchingMapping) {
      return matchingMapping[1]; // Return the canonical value
    }

    // If no explicit mapping exists, normalize by returning the value as-is
    // but we'll group by lowercase in the reduce function
    return trimmedValue;
  };

  // Analytics calculations with case-insensitive grouping
  const analyticsByAgency = useMemo(() => {
    const grouped = closes.reduce((acc, close) => {
      const mappedValue = applyMapping(close.agency, 'agency');
      // When normalized, group by lowercase to combine case variations
      const key = useNormalized ? mappedValue.toLowerCase() : mappedValue;

      if (!acc[key]) {
        acc[key] = { name: mappedValue, total: 0, count: 0 };
      } else if (useNormalized) {
        // Use the more common capitalization by comparing counts
        // This ensures we display "Foresters" not "foresters" if it's more common
        if (acc[key].count === 0 || mappedValue === acc[key].name) {
          acc[key].name = mappedValue;
        }
      }

      acc[key].total += Number(close.ap) || 0;
      acc[key].count += 1;
      return acc;
    }, {});
    return Object.values(grouped);
  }, [closes, mappings, useNormalized]);

  const analyticsByLeadType = useMemo(() => {
    const grouped = closes.reduce((acc, close) => {
      const mappedValue = applyMapping(close.lead_type, 'lead_type');
      const key = useNormalized ? mappedValue.toLowerCase() : mappedValue;

      if (!acc[key]) {
        acc[key] = { name: mappedValue, total: 0, count: 0 };
      } else if (useNormalized && mappedValue === acc[key].name) {
        acc[key].name = mappedValue;
      }

      acc[key].total += Number(close.ap) || 0;
      acc[key].count += 1;
      return acc;
    }, {});
    return Object.values(grouped);
  }, [closes, mappings, useNormalized]);

  const analyticsByProduct = useMemo(() => {
    const grouped = closes.reduce((acc, close) => {
      const mappedValue = applyMapping(close.product, 'product');
      const key = useNormalized ? mappedValue.toLowerCase() : mappedValue;

      if (!acc[key]) {
        acc[key] = { name: mappedValue, total: 0, count: 0 };
      } else if (useNormalized && mappedValue === acc[key].name) {
        acc[key].name = mappedValue;
      }

      acc[key].total += Number(close.ap) || 0;
      acc[key].count += 1;
      return acc;
    }, {});
    return Object.values(grouped);
  }, [closes, mappings, useNormalized]);

  const analyticsByCarrier = useMemo(() => {
    const grouped = closes.reduce((acc, close) => {
      const mappedValue = applyMapping(close.carrier, 'carrier');
      const key = useNormalized ? mappedValue.toLowerCase() : mappedValue;

      if (!acc[key]) {
        acc[key] = { name: mappedValue, total: 0, count: 0 };
      } else if (useNormalized && mappedValue === acc[key].name) {
        acc[key].name = mappedValue;
      }

      acc[key].total += Number(close.ap) || 0;
      acc[key].count += 1;
      return acc;
    }, {});
    return Object.values(grouped);
  }, [closes, mappings, useNormalized]);

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortData = (data) => {
    const sorted = [...data].sort((a, b) => {
      const aValue = sortConfig.key === 'name' ? a.name : a[sortConfig.key];
      const bValue = sortConfig.key === 'name' ? b.name : b[sortConfig.key];

      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const renderAnalyticsTable = (data, title, icon) => {
    const sortedData = sortData(data);
    const total = data.reduce((sum, item) => sum + item.total, 0);
    const totalCount = data.reduce((sum, item) => sum + item.count, 0);

    return (
      <TableContainer component={Paper} sx={{ mt: 3 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="h6">{title}</Typography>
        </Box>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'name'}
                  direction={sortConfig.key === 'name' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  {t('common.name')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortConfig.key === 'count'}
                  direction={sortConfig.key === 'count' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('count')}
                >
                  {t('common.count')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortConfig.key === 'total'}
                  direction={sortConfig.key === 'total' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('total')}
                >
                  {t('analytics.totalAP')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortConfig.key === 'average'}
                  direction={sortConfig.key === 'average' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('average')}
                >
                  {t('analytics.avgAP')}
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">{t('analytics.percentOfTotal')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((item, index) => {
              const average = item.count > 0 ? item.total / item.count : 0;
              const percentage = total > 0 ? (item.total / total) * 100 : 0;
              return (
                <TableRow key={index} hover>
                  <TableCell>{item.name}</TableCell>
                  <TableCell align="right">{item.count}</TableCell>
                  <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                  <TableCell align="right">{formatCurrency(average)}</TableCell>
                  <TableCell align="right">{percentage.toFixed(1)}%</TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ backgroundColor: theme.palette.action.hover, fontWeight: 'bold' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>{t('common.total')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totalCount}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(total)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(totalCount > 0 ? total / totalCount : 0)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>100%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="text.primary">
        {t('analytics.title')}
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('common.dateType')}</InputLabel>
              <Select
                name="dateType"
                value={filters.dateType}
                label={t('common.dateType')}
                onChange={handleFilterChange}
              >
                <MenuItem value="submit">{t('common.submitDate')}</MenuItem>
                <MenuItem value="effective">{t('common.effectiveDate')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePickerField
              label={t('common.startDate')}
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DatePickerField
              label={t('common.endDate')}
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Tooltip title={t('analytics.useNormalizedTooltip')}>
              <FormControlLabel
                control={
                  <Switch
                    checked={useNormalized}
                    onChange={(e) => setUseNormalized(e.target.checked)}
                    color="primary"
                  />
                }
                label={t('analytics.useNormalizedData')}
              />
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('analytics.totalCloses')}
              </Typography>
              <Typography variant="h4">{closes.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('analytics.totalAP')}
              </Typography>
              <Typography variant="h4">
                {formatCurrency(closes.reduce((sum, c) => sum + (Number(c.ap) || 0), 0))}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('analytics.uniqueAgencies')}
              </Typography>
              <Typography variant="h4">{analyticsByAgency.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                {t('analytics.avgAPPerClose')}
              </Typography>
              <Typography variant="h4">
                {formatCurrency(closes.length > 0
                  ? closes.reduce((sum, c) => sum + (Number(c.ap) || 0), 0) / closes.length
                  : 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for different analytics views */}
      <Paper>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={t('analytics.byAgency')} />
          <Tab label={t('analytics.byLeadType')} />
          <Tab label={t('analytics.byProduct')} />
          <Tab label={t('analytics.byCarrier')} />
        </Tabs>

        {activeTab === 0 && renderAnalyticsTable(
          analyticsByAgency,
          t('analytics.analyticsByAgency'),
          <BusinessIcon color="primary" />
        )}
        {activeTab === 1 && renderAnalyticsTable(
          analyticsByLeadType,
          t('analytics.analyticsByLeadType'),
          <LabelIcon color="primary" />
        )}
        {activeTab === 2 && renderAnalyticsTable(
          analyticsByProduct,
          t('analytics.analyticsByProduct'),
          <CategoryIcon color="primary" />
        )}
        {activeTab === 3 && renderAnalyticsTable(
          analyticsByCarrier,
          t('analytics.analyticsByCarrier'),
          <TrendingUpIcon color="primary" />
        )}
      </Paper>
    </Box>
  );
};

export default Analytics;
